import { t } from '../../i18n/i18n';
import type { PathNode } from '../geometry/bezier';
import type { Vec } from '../geometry/vec';
import { IDENTITY } from './geometry';
import { defaultMachine, defaultParams } from './params';
import type { AreaShape, ColumnShape, Design, EmbObject, LineShape, Shape, StitchParams, StitchType, ThreadColor } from './types';

let counter = 0;
/** Unique id; uses crypto.randomUUID when available. */
export function newId(prefix = 'id'): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return `${prefix}_${c.randomUUID().slice(0, 13)}`;
  counter++;
  return `${prefix}_${Date.now().toString(36)}${counter.toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export function createDesign(partial: Partial<Design> = {}): Design {
  return {
    name: t('Untitled'),
    widthMm: 100,
    heightMm: 100,
    palette: [],
    objects: [],
    machine: defaultMachine(),
    backdrop: null,
    ...partial,
  };
}

export function createThread(hex: string, name?: string): ThreadColor {
  return { id: newId('c'), name: name ?? hex.toUpperCase(), hex: hex.toLowerCase() };
}

export function createObject(shape: Shape, params: StitchParams | StitchType, colorId: string, name?: string): EmbObject {
  const p = typeof params === 'string' ? defaultParams(params) : params;
  return {
    id: newId('o'),
    name: name ?? defaultName(shape, p.type),
    colorId,
    visible: true,
    locked: false,
    shape,
    transform: { ...IDENTITY },
    params: p,
    entry: null,
    exit: null,
    tieIn: true,
    tieOff: true,
  };
}

const TYPE_NAMES: Record<StitchType, string> = { running: 'Running stitch', fill: 'Fill', satin: 'Satin' };
export const stitchTypeName = (type: StitchType): string => t(TYPE_NAMES[type]);

function defaultName(shape: Shape, type: StitchType): string {
  const kind = shape.kind === 'area' ? t('Area') : shape.kind === 'line' ? t('Line') : t('Column');
  return `${kind} (${stitchTypeName(type).toLowerCase()})`;
}

// ---------------------------------------------------------------- shape builders (mm)

export function rectShape(x: number, y: number, w: number, h: number): AreaShape {
  return {
    kind: 'area',
    outer: [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ],
    holes: [],
  };
}

/** Ellipse as four cubic Bézier segments (kappa approximation, error < 0.03 %). */
export function ellipseNodes(cx: number, cy: number, rx: number, ry: number): PathNode[] {
  const k = 0.5522847498;
  return [
    { x: cx + rx, y: cy, in: { x: cx + rx, y: cy - ry * k }, out: { x: cx + rx, y: cy + ry * k } },
    { x: cx, y: cy + ry, in: { x: cx + rx * k, y: cy + ry }, out: { x: cx - rx * k, y: cy + ry } },
    { x: cx - rx, y: cy, in: { x: cx - rx, y: cy + ry * k }, out: { x: cx - rx, y: cy - ry * k } },
    { x: cx, y: cy - ry, in: { x: cx - rx * k, y: cy - ry }, out: { x: cx + rx * k, y: cy - ry } },
  ];
}

export function ellipseShape(cx: number, cy: number, rx: number, ry: number): AreaShape {
  return { kind: 'area', outer: ellipseNodes(cx, cy, rx, ry), holes: [] };
}

export function polygonShape(outer: Vec[], holes: Vec[][] = []): AreaShape {
  return { kind: 'area', outer: outer.map((p) => ({ x: p.x, y: p.y })), holes: holes.map((h) => h.map((p) => ({ x: p.x, y: p.y }))) };
}

export function lineShape(points: Vec[], closed = false): LineShape {
  return { kind: 'line', nodes: points.map((p) => ({ x: p.x, y: p.y })), closed };
}

export function columnShape(railA: Vec[], railB: Vec[]): ColumnShape {
  return { kind: 'column', railA: railA.map((p) => ({ ...p })), railB: railB.map((p) => ({ ...p })) };
}

/** Which stitch types make sense for a shape kind. */
export function allowedStitchTypes(kind: Shape['kind'], closed = true): StitchType[] {
  switch (kind) {
    case 'area':
      return ['fill', 'satin', 'running'];
    case 'line':
      return closed ? ['running', 'satin', 'fill'] : ['running', 'satin'];
    case 'column':
      return ['satin', 'fill', 'running'];
  }
}
